import { apiFetch } from "./client";

/* ---------- Response types ---------- */

export interface SubscriptionDetail {
  id: string;
  plan_type: string;
  status: string;
  stripe_customer_id: string;
  stripe_subscription_id: string | null;
  current_period_start: string | null;
  current_period_end: string | null;
}

export interface TokenBalanceDetail {
  subscription_tokens_used: number;
  subscription_tokens_limit: number;
  topup_tokens_remaining: number;
}

export interface TokenLedgerEntry {
  id: string;
  entry_type: string;
  amount: number;
  balance_after: number;
  description: string | null;
  created_at: string;
}

export interface WebhookEvent {
  id: string;
  event_id: string;
  event_type: string;
  status: string;
  error_message: string | null;
  received_at: string;
  processed_at: string | null;
}

export interface UsageEventEntry {
  id: string;
  source: string;
  resource_type: string;
  provider: string | null;
  model: string | null;
  status: string;
  billable_units: number;
  price_version: string;
  created_at: string;
  finalized_at: string | null;
}

export interface PaymentTransactionEntry {
  id: string;
  provider: string;
  provider_environment: string;
  provider_transaction_id: string;
  provider_original_transaction_id: string | null;
  hashee_sku_id: string;
  provider_product_id: string;
  status: string;
  raw_receipt_ref: string;
  entitlement_event_id: string | null;
  created_at: string;
  verified_at: string | null;
}

export interface EntitlementEventEntry {
  id: string;
  source_transaction_id: string;
  reverses_event_id: string | null;
  event_type: string;
  plan_type: string | null;
  token_credit_delta: number | null;
  storage_limit_bytes: number | null;
  preview_compute_minutes_delta: number | null;
  effective_at: string;
  created_at: string;
}

export interface UserBillingData {
  subscription: SubscriptionDetail | null;
  token_balance: TokenBalanceDetail | null;
  recent_ledger: TokenLedgerEntry[];
  recent_webhooks: WebhookEvent[];
  recent_usage_events: UsageEventEntry[];
  recent_payment_transactions: PaymentTransactionEntry[];
  recent_entitlement_events: EntitlementEventEntry[];
}

interface GrantTokensResponse {
  ledger_entry_id: string;
  new_balance: number;
}

interface ResyncResponse {
  synced: boolean;
}

interface ReplayResponse {
  replayed: boolean;
  new_status: string;
}

export type DeveloperCreditPayload = {
  readonly amount_minor: number;
  readonly currency: string;
  readonly reason: string;
};

export type DeveloperCreditResponse = {
  readonly ledger_entry_id: string;
  readonly developer_user_id: string;
  readonly amount_minor: number;
  readonly balance_after_minor: number;
  readonly replayed: boolean;
};

export type AgentSubscriptionPriceMappingPayload = {
  readonly hashee_sku_id: string;
  readonly stripe_price_id: string;
  readonly stripe_product_id: string;
  readonly reason: string;
};

export type AgentSubscriptionPriceMappingResult = {
  readonly hashee_sku_id: string;
  readonly stripe_price_id: string;
  readonly stripe_product_id: string;
  readonly updated_at: string;
};

/* ---------- API calls ---------- */

export async function fetchUserBilling(
  userId: string,
): Promise<UserBillingData> {
  const res = await apiFetch<{ data: UserBillingData }>(
    `/admin/billing/users/${userId}`,
  );
  return res.data;
}

export async function grantTokens(
  userId: string,
  amount: number,
  reason: string,
): Promise<GrantTokensResponse> {
  const res = await apiFetch<{ data: GrantTokensResponse }>(
    `/admin/billing/users/${userId}/grant-tokens`,
    {
      method: "POST",
      body: JSON.stringify({ amount, reason }),
    },
  );
  return res.data;
}

export async function resyncEntitlement(
  userId: string,
  reason: string,
): Promise<ResyncResponse> {
  const res = await apiFetch<{ data: ResyncResponse }>(
    `/admin/billing/users/${userId}/resync-entitlement`,
    {
      method: "POST",
      body: JSON.stringify({ reason }),
    },
  );
  return res.data;
}

export async function replayWebhook(
  webhookEventId: string,
  reason: string,
): Promise<ReplayResponse> {
  const res = await apiFetch<{ data: ReplayResponse }>(
    "/admin/billing/webhook/replay",
    {
      method: "POST",
      body: JSON.stringify({ webhook_event_id: webhookEventId, reason }),
    },
  );
  return res.data;
}

export async function creditDeveloper(
  developerUserId: string,
  payload: DeveloperCreditPayload,
): Promise<DeveloperCreditResponse> {
  const idempotencyKey = createDeveloperCreditIdempotencyKey();
  const res = await apiFetch<{ data: DeveloperCreditResponse }>(
    `/admin/billing/developers/${developerUserId}/credits`,
    {
      method: "POST",
      headers: { "X-Idempotency-Key": idempotencyKey },
      body: JSON.stringify({
        amount_minor: payload.amount_minor,
        currency: payload.currency,
        reason: payload.reason,
      }),
    },
  );
  return res.data;
}

export async function upsertAgentSubscriptionPriceMapping(
  payload: AgentSubscriptionPriceMappingPayload,
): Promise<AgentSubscriptionPriceMappingResult> {
  const res = await apiFetch<{ data: AgentSubscriptionPriceMappingResult }>(
    "/admin/billing/agent-commerce/subscription-prices",
    {
      method: "POST",
      body: JSON.stringify(payload),
    },
  );
  return res.data;
}

function createDeveloperCreditIdempotencyKey(): string {
  if (globalThis.crypto.randomUUID) {
    return `admin-developer-credit-${globalThis.crypto.randomUUID()}`;
  }

  const bytes = new Uint8Array(16);
  globalThis.crypto.getRandomValues(bytes);
  const suffix = Array.from(bytes, (byte) =>
    byte.toString(16).padStart(2, "0"),
  ).join("");
  return `admin-developer-credit-${suffix}`;
}
